import type { FirebaseApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';
import type { WebPushPlatform } from '../push/controller';

/** SDK v11 del repositorio: su token FCM es el contrato actual del backend.
 * https://firebase.google.com/docs/cloud-messaging/web/get-started
 * No mezcla los nuevos FID con el endpoint fcm_token existente.
 */
export function createFirebaseWebPush(app: FirebaseApp, vapidKey: string): WebPushPlatform {
  let registration: Promise<ServiceWorkerRegistration> | null = null;
  return {
    supported: async () => !!vapidKey && typeof window !== 'undefined' && window.isSecureContext &&
      'Notification' in window && 'serviceWorker' in navigator && !!navigator.locks && await isSupported(),
    permission: () => typeof Notification === 'undefined' ? 'denied' : Notification.permission,
    requestPermission: () => Notification.requestPermission(),
    token: async () => {
      // Ámbito propio sin fetch handler: no sustituye el worker de la web Flutter.
      registration ??= navigator.serviceWorker.register('/ibercar-push-sw.js', { scope: '/push/' }).catch(error => {
        registration = null; throw error;
      });
      const worker = await registration;
      if (!worker.active) {
        const candidate = worker.installing || worker.waiting;
        if (!candidate) throw new Error('El navegador no pudo preparar los avisos.');
        await new Promise<void>((resolve, reject) => {
          const timer = window.setTimeout(() => { candidate.removeEventListener('statechange', changed); reject(new Error('No se pudo activar el receptor de avisos.')); }, 15000);
          function changed() {
            if (candidate!.state === 'activated' || candidate!.state === 'redundant') {
              window.clearTimeout(timer); candidate!.removeEventListener('statechange', changed);
              if (candidate!.state === 'activated') resolve(); else reject(new Error('El navegador rechazó el receptor de avisos.'));
            }
          }
          candidate.addEventListener('statechange', changed); changed();
        });
      }
      return getToken(getMessaging(app), { vapidKey, serviceWorkerRegistration: worker });
    },
    deleteToken: async () => { if (await isSupported()) await deleteToken(getMessaging(app)); },
    exclusive: async action => await navigator.locks.request('ibercar-native-web-push', { mode: 'exclusive' }, action),
  };
}

/** Mensaje local sin datos personales; el buzón vuelve a consultar su autoridad. */
export function onWebPushNotice(onNotice: () => void): () => void {
  return onWorkerMessage('ibercar-push-notice', onNotice);
}
/** El worker no guarda un JWT: si cambia la suscripción, la página renueva con su sesión. */
export function onWebPushTokenRefresh(onRefresh: () => void): () => void {
  return onWorkerMessage('ibercar-push-refresh', onRefresh);
}
function onWorkerMessage(type: string, callback: () => void): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const receive = (event: MessageEvent) => {
    const source = event.source as ServiceWorker | null;
    if (event.data?.type !== type || !source?.scriptURL) return;
    const url = new URL(source.scriptURL);
    if (url.origin === location.origin && url.pathname === '/ibercar-push-sw.js') callback();
  };
  navigator.serviceWorker.addEventListener('message', receive);
  return () => navigator.serviceWorker.removeEventListener('message', receive);
}
